import { useExchangeMarketHours } from "./useExchangeMarketHours";
import { getExchangeTradingHours } from "../../../../utils/exchangeTimezone";

export interface ExchangeTradingMinutes {
  openMinutes: number;
  closeMinutes: number;
}

// FMP reports hours as "09:30 AM -04:00" / "04:00 PM -04:00". Only the clock
// part matters here — candle timestamps are already parsed in the exchange's
// timezone, so we compare minutes-of-day. Returns null on an unrecognised
// string so the caller can fall back.
const parseClockToMinutes = (value: string | undefined): number | null => {
  if (!value) return null;
  const match = value.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!match) return null;
  let hours = parseInt(match[1], 10) % 12;
  const minutes = parseInt(match[2], 10);
  if (match[3].toUpperCase() === "PM") hours += 12;
  return hours * 60 + minutes;
};

/**
 * Resolve an exchange's current regular-session bounds as minutes-from-midnight
 * in exchange-local time. Uses FMP's live exchange-market-hours (accurate for
 * half-days, holiday early closes, and DST), falling back to the static
 * per-exchange table while the request is in flight or if FMP has no data.
 */
export const useExchangeTradingMinutes = (
  exchange: string | undefined | null
): ExchangeTradingMinutes => {
  const { data } = useExchangeMarketHours(exchange ?? undefined);
  const fallback = getExchangeTradingHours(exchange);

  return {
    openMinutes: parseClockToMinutes(data?.openingHour) ?? fallback.openMinutes,
    closeMinutes:
      parseClockToMinutes(data?.closingHour) ?? fallback.closeMinutes,
  };
};
